import type { ShopperEvent } from "@sirdab/returns-shared";
import { cn } from "@sirdab/ui";

import { formatCount, formatDateTime } from "../format/money";
import { useMessages } from "../i18n";
import { usePortalCopy } from "../i18n/usePortalCopy";

export interface TimelineProps {
  events: readonly ShopperEvent[];
}

/**
 * The tracking timeline (docs/08 step 8), read from the return's event log as the server
 * projects it for a shopper (`ShopperEventSchema`): every step with its time, who did it,
 * and the few facts they may read. The shopper's own note and photo count sit under the
 * request; a rejection reason renders in full under the refusal (CLAUDE.md rule 5).
 *
 * `received` is deliberately given weight: it is the one moment a platform without a 3PL
 * behind it cannot show a shopper. Everything else is a quiet row.
 *
 * Every label is server-authored and bilingual: rendered, never translated here. The
 * rail sits on the logical start side (`ps-`), so Arabic needs no override.
 */
export function Timeline({ events }: TimelineProps): React.JSX.Element {
  const { text, locale, t } = useMessages();
  const c = usePortalCopy();

  if (events.length === 0) {
    return (
      <p className="text-body text-muted-foreground">
        {t("track.empty_timeline")}
      </p>
    );
  }

  return (
    <ol className="flex flex-col">
      {events.map((event, index) => {
        const received = event.toState === "received";
        const { detail } = event;
        return (
          <li
            key={`${event.type}-${event.at}-${index}`}
            data-state={event.toState ?? undefined}
            data-event-type={event.type}
            className="relative flex gap-3 ps-1"
          >
            <span aria-hidden="true" className="flex flex-col items-center">
              <span
                className={cn(
                  "mt-1.5 block rounded-full",
                  received ? "bg-primary size-3" : "bg-gray-alpha-400 size-2.5",
                )}
              />
              {index < events.length - 1 ? (
                <span className="bg-border w-px flex-1" />
              ) : null}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 pb-4">
              <p
                className={cn(
                  "text-body text-foreground",
                  received && "font-semibold",
                )}
              >
                {text(event.label)}
              </p>
              <p className="text-caption-quiet tabular text-subtle">
                <span dir="ltr">{formatDateTime(event.at, locale)}</span>
                {" · "}
                {t(`actor.${event.actorType}`)}
              </p>
              {detail.note !== undefined ? (
                <blockquote className="bg-surface-whisper text-body-quiet text-muted-foreground mt-1 rounded-md px-3 py-2">
                  <span className="text-caption-quiet text-subtle block">
                    {c("track.your_words")}
                  </span>
                  {detail.note}
                </blockquote>
              ) : null}
              {detail.photoCount !== undefined && detail.photoCount > 0 ? (
                <p className="text-caption-quiet text-muted-foreground">
                  {c("track.photos_attached")}:{" "}
                  <span dir="ltr" className="tabular-nums">
                    {formatCount(detail.photoCount, locale)}
                  </span>
                </p>
              ) : null}
              {detail.qtyReceived !== undefined ? (
                <p className="text-caption-quiet text-muted-foreground">
                  {t("track.qty_received")}{" "}
                  <span dir="ltr" className="tabular-nums">
                    {formatCount(detail.qtyReceived, locale)}
                  </span>
                </p>
              ) : null}
              {detail.grade !== undefined ? (
                <p className="text-caption-quiet text-muted-foreground">
                  {t("track.grade_label")}: {t(`grade.${detail.grade}`)}
                </p>
              ) : null}
              {detail.rejectionReason !== undefined ? (
                <p className="text-caption text-destructive mt-1">
                  {text(detail.rejectionReason)}
                </p>
              ) : null}
              {received ? (
                <p className="text-caption-quiet text-muted-foreground mt-1">
                  {t("track.received_note")}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
